// Utility functions to handle website data in localStorage and store images.
// Images are stored either in localStorage under keys `bluecity-img-<id>`
// or (preferred for larger data) inside IndexedDB object store `images`.
// News items continue to reference images as "imgref:<id>"; the storage layer
// resolves the ref from either storage location.

const NEWS_KEY = 'bluecity-news';
const WEBSITE_KEY = 'bluecity-website';
const IMG_PREFIX = 'bluecity-img-';

const IDB_DB_NAME = 'bluecity-images-db';
const IDB_STORE_NAME = 'images';

const generateId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

function makeImgKey(id) {
  return `${IMG_PREFIX}${id}`;
}

function isDataUrl(str) {
  return typeof str === 'string' && str.startsWith('data:');
}

// --- IndexedDB small helper (promise wrapper) ---
function openImageDB() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(new Error('IndexedDB not supported'));
    const req = window.indexedDB.open(IDB_DB_NAME, 1);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open IDB'));
  });
}

function idbPutImage(id, dataUrl) {
  return openImageDB().then(db => new Promise((res, rej) => {
    const tx = db.transaction([IDB_STORE_NAME], 'readwrite');
    const store = tx.objectStore(IDB_STORE_NAME);
    store.put({ id, data: dataUrl });
    tx.oncomplete = () => {
      db.close();
      res(true);
    };
    tx.onerror = () => {
      db.close();
      rej(tx.error || new Error('IDB put failed'));
    };
  }));
}

function idbGetImage(id) {
  return openImageDB().then(db => new Promise((res, rej) => {
    const tx = db.transaction([IDB_STORE_NAME], 'readonly');
    const store = tx.objectStore(IDB_STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => {
      db.close();
      if (req.result && req.result.data) res(req.result.data);
      else res('');
    };
    req.onerror = () => {
      db.close();
      rej(req.error || new Error('IDB get failed'));
    };
  }));
}

function idbDeleteImage(id) {
  return openImageDB().then(db => new Promise((res, rej) => {
    const tx = db.transaction([IDB_STORE_NAME], 'readwrite');
    const store = tx.objectStore(IDB_STORE_NAME);
    store.delete(id);
    tx.oncomplete = () => { db.close(); res(true); };
    tx.onerror = () => { db.close(); rej(tx.error || new Error('IDB delete failed')); };
  }));
}

/**
 * Remove up to `count` oldest image keys (best-effort) in localStorage to free space.
 * Keeps the original best-effort approach for localStorage cleanup.
 */
function freeSomeImageStorage(count = 6) {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(IMG_PREFIX)) keys.push(k);
    }
    for (let i = 0; i < Math.min(count, keys.length); i++) {
      try { localStorage.removeItem(keys[i]); } catch (_) { /* ignore */ }
    }
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Try to store an image under its image key.
 * Strategy:
 *  1) try localStorage (existing behavior) — fast and simple
 *  2) on quota failure, attempt cleanup and retry localStorage
 *  3) if still failing or if localStorage unsupported, fall back to IndexedDB
+ * Returns boolean. If IDB is used it runs asynchronously in background.
 */
function storeImageForItem(id, imageData) {
  if (!id || !imageData) return false;
  const key = makeImgKey(id);

  // try synchronous localStorage path first
  try {
    localStorage.setItem(key, imageData);
    return true;
  } catch (e) {
    if (e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
      // try freeing some stored images then retry
      try {
        freeSomeImageStorage(8);
        localStorage.setItem(key, imageData);
        return true;
      } catch (e2) {
        try {
          freeSomeImageStorage(20);
          localStorage.setItem(key, imageData);
          return true;
        } catch (e3) {
          // fall back to IndexedDB asynchronously
          try {
            idbPutImage(id, imageData).catch(err => console.warn('idbPutImage failed', err));
            // return true because persistence was scheduled
            return true;
          } catch (idbErr) {
            console.warn('storeImageForItem: failed to persist image for', id, idbErr);
            return false;
          }
        }
      }
    }
    // other errors — try IDB in background and return true if scheduled
    try {
      idbPutImage(id, imageData).catch(err => console.warn('idbPutImage failed', err));
      return true;
    } catch (idbErr) {
      console.warn('storeImageForItem: unexpected error and IDB fallback failed for', id, idbErr);
      return false;
    }
  }
}

/**
 * Read image data for an imgref:<id>.
 * Keeps synchronous API: try localStorage first. If not present, return '' and
 * attempt to migrate from IndexedDB into localStorage asynchronously (best-effort).
 */
function getImageForItemRef(ref) {
  if (typeof ref !== 'string') return '';
  const match = ref.match(/^imgref:(.+)$/);
  if (!match) return '';
  const id = match[1];
  const key = makeImgKey(id);
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
  } catch (e) {
    // ignore localStorage read errors
  }
  // If not in localStorage, try to fetch from IndexedDB and copy it into localStorage
  // for subsequent synchronous reads. This runs asynchronously and does not block.
  idbGetImage(id).then(data => {
    if (data) {
      try {
        localStorage.setItem(key, data);
      } catch (e) {
        // ignore: cannot migrate into localStorage
      }
    }
  }).catch(() => { /* ignore */ });
  return '';
}

/**
 * Remove image key for a given ref (safe) from both localStorage and IndexedDB.
 */
function removeImageForItemRef(ref) {
  if (typeof ref !== 'string') return false;
  const match = ref.match(/^imgref:(.+)$/);
  if (!match) return false;
  const id = match[1];
  try {
    localStorage.removeItem(makeImgKey(id));
  } catch (_) { /* ignore */ }
  // delete from IDB asynchronously
  idbDeleteImage(id).catch(() => { /* ignore */ });
  return true;
}

// read news data and resolve image references into actual data-URLs for UI
export const getNewsData = () => {
  try {
    const raw = localStorage.getItem(NEWS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map(item => {
      const copy = { ...item };
      if (typeof copy.image === 'string' && copy.image.startsWith('imgref:')) {
        // synchronous resolution: this will read from localStorage only.
        // If the image is only present in IndexedDB it will be migrated in background
        // by getImageForItemRef but won't be available synchronously here.
        const dataUrl = getImageForItemRef(copy.image);
        copy.image = dataUrl || ''; // replace ref with actual data url (or empty)
      }
      return copy;
    });
  } catch (e) {
    console.error('Failed to parse news data', e);
    return [];
  }
};

// Async version of getNewsData which will hydrate images from IndexedDB when needed.
export const getNewsDataAsync = async () => {
  try {
    const raw = localStorage.getItem(NEWS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const out = [];
    for (const item of arr) {
      const copy = { ...item };
      if (typeof copy.image === 'string' && copy.image.startsWith('imgref:')) {
        const match = copy.image.match(/^imgref:(.+)$/);
        if (match) {
          const id = match[1];
          // try localStorage first
          try {
            const key = makeImgKey(id);
            const ls = localStorage.getItem(key);
            if (ls) {
              copy.image = ls;
            } else {
              // fetch from IndexedDB
              try {
                const data = await idbGetImage(id);
                copy.image = data || '';
                // attempt best-effort migration into localStorage for faster future reads
                if (data) {
                  try { localStorage.setItem(key, data); } catch (_) { /* ignore */ }
                }
              } catch (_) {
                copy.image = '';
              }
            }
          } catch (_) {
            copy.image = '';
          }
        }
      }
      out.push(copy);
    }
    return out;
  } catch (e) {
    console.error('Failed to parse news data (async)', e);
    return [];
  }
};

// save news array — store any inline data-URL images separately and replace with imgref:<id>
export const saveNewsData = (news) => {
  if (!Array.isArray(news)) return false;
  // ensure each item has id
  const toSave = news.map(item => {
    const it = { ...item };
    if (!it.id) it.id = generateId();
    // if image is a data URL, persist under image key and replace with ref
    if (isDataUrl(it.image)) {
      try {
        storeImageForItem(it.id, it.image);
        it.image = `imgref:${it.id}`;
      } catch {
        // ignore failure; keep data in item as fallback
      }
    }
    return it;
  });

  try {
    localStorage.setItem(NEWS_KEY, JSON.stringify(toSave));
    return true;
  } catch (err) {
    console.warn('Failed to save news data to localStorage', err);
    return false;
  }
};

// add news (no limits)
export const addLatestNews = (newsItem) => {
  const all = getNewsData(); // returns items with resolved images
  const id = newsItem.id || generateId();
  const item = {
    id,
    title: newsItem.title || '',
    excerpt: newsItem.excerpt || '',
    category: newsItem.category || [],
    image: '', // will be set below
    type: newsItem.type || 'latest',
    date: newsItem.date || new Date().toLocaleString()
  };

  // if incoming has data-url image, store it and set ref; if has imgref use as-is
  if (isDataUrl(newsItem.image)) {
    // persist (may fall back to IndexedDB asynchronously) and store a ref in the item
    storeImageForItem(id, newsItem.image);
    item.image = `imgref:${id}`;
  } else if (typeof newsItem.image === 'string' && newsItem.image.startsWith('imgref:')) {
    item.image = newsItem.image;
  } else if (typeof newsItem.image === 'string' && newsItem.image) {
    // maybe a direct data-url already resolved; try to store
    if (isDataUrl(newsItem.image)) {
      storeImageForItem(id, newsItem.image);
      item.image = `imgref:${id}`;
    } else {
      item.image = ''; // unknown format, don't persist
    }
  }

  all.unshift(item);
  saveNewsData(all);
  // return persisted item with resolved image for UI convenience.
  // If the incoming newsItem included a data-URL, prefer returning that data-URL
  // immediately so the UI can show it even if persistence used IndexedDB.
  const returned = { ...item };
  if (isDataUrl(newsItem.image)) {
    returned.image = newsItem.image;
  } else if (returned.image.startsWith('imgref:')) {
    returned.image = getImageForItemRef(returned.image);
  }
  return returned;
};

export const updateNews = (id, updatedNews) => {
  const allRaw = localStorage.getItem(NEWS_KEY);
  let all = [];
  try {
    all = allRaw ? JSON.parse(allRaw) : [];
  } catch {
    all = [];
  }
  const idx = all.findIndex(i => i.id === id);
  if (idx === -1) return null;

  const merged = { ...all[idx], ...updatedNews, id };
  // handle image: if updatedNews.image is a data-url store it and set imgref
  if (isDataUrl(updatedNews.image)) {
    storeImageForItem(id, updatedNews.image);
    // keep imgref for persistence, but return the data-url immediately for the UI
    merged.image = `imgref:${id}`;
  } else if (typeof updatedNews.image === 'string' && updatedNews.image === '') {
    // explicit clear
    removeImageForItemRef(all[idx].image);
    merged.image = '';
  } else if (typeof updatedNews.image === 'string' && updatedNews.image.startsWith('imgref:')) {
    merged.image = updatedNews.image;
  }
  all[idx] = merged;
  try {
    localStorage.setItem(NEWS_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn('Failed to update news in localStorage', e);
  }
  // return merged with resolved image
  const result = { ...merged };
  // if caller passed a data-url, return it for immediate display
  if (isDataUrl(updatedNews.image)) {
    result.image = updatedNews.image;
  } else if (result.image && result.image.startsWith('imgref:')) {
    result.image = getImageForItemRef(result.image);
  }
  return result;
};

export const deleteNews = (id) => {
  const raw = localStorage.getItem(NEWS_KEY);
  let arr = [];
  try {
    arr = raw ? JSON.parse(raw) : [];
  } catch {
    arr = [];
  }
  const found = arr.find(i => i.id === id);
  if (found && typeof found.image === 'string' && found.image.startsWith('imgref:')) {
    removeImageForItemRef(found.image);
  }
  const filtered = arr.filter(item => item.id !== id);
  try {
    localStorage.setItem(NEWS_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.warn('Failed to save news after delete', e);
  }
  return true;
};

export const getFeaturedNews = () => {
  // return only items explicitly marked as 'featured'
  try {
    return getNewsData()
  } catch (e) {
    return [];
  }
};

export const getLatestNews = () => {
  
  try {
    return getNewsData()
  } catch (e) {
    return [];
  }
};

export const getNewsByCategory = (category) => {
  const all = getNewsData();
  return all.filter(item => {
    if (Array.isArray(item.category)) {
      return item.category.some(c => String(c).toLowerCase() === String(category).toLowerCase());
    }
    if (typeof item.category === 'string') {
      return item.category.toLowerCase() === category.toLowerCase();
    }
    return false;
  });
};

// website data helpers (simple persistent storage)
export const getWebsiteData = () => {
  try {
    const raw = localStorage.getItem(WEBSITE_KEY);
    if (!raw) return { logo: '', cityHighlight: '', aboutContent: '', quickLinks: [], weather: {}, featuredNews: [], latestNews: [] };
    return JSON.parse(raw);
  } catch {
    return { logo: '', cityHighlight: '', aboutContent: '', quickLinks: [], weather: {}, featuredNews: [], latestNews: [] };
  }
};

export const saveWebsiteData = (data) => {
  try {
    localStorage.setItem(WEBSITE_KEY, JSON.stringify(data));
    return true;
  } catch (e) {
    console.warn('Failed to save website data', e);
    return false;
  }
};    